import { type SyncedVisualisationProps, type VisualisationProps } from '@soulhx/fs-common';
import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';
import { db, type ItemSyncStatus, type LocalGraph } from '../model/db.js';
import { triggerGlobalSync } from './GlobalSyncMiddleware.js';
import { syncFetch, syncPull } from './syncFetch.js';
import { nextSyncCursor, pullSince, toUtcIso } from './timestamps.js';

export const GRAPH_SYNC_POLL_INTERVAL_MS = 20_000;
export const GRAPH_SYNC_KEY = 'graphs';

const GRAPHS_PATH = '/do/g';

// see SettingSync.ts - passing `since` at all (even this far back) routes
// the server to the sync-oriented delta endpoint on the very first pull
const EPOCH = new Date(0).toISOString();

const isUnsynced = (status: ItemSyncStatus | undefined): boolean => status !== undefined && status !== 'synced';

const logSyncError = (context: string, error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`graph sync: ${context}`, error);
};

// strips the local-only bookkeeping fields (id/updateDate/syncStatus for a
// create, since the server mints its own id; updateDate/syncStatus always,
// since the server owns both) before sending a graph back over the wire.
// `deleted` isn't a `LocalGraph` field, but is stripped defensively too,
// since a row that started life as a `SyncedVisualisationProps` pulled from
// the server (or a test/other caller that built one the same way) could
// still be carrying it as a stray property.
const toWireProps = (local: LocalGraph, keepId: boolean): VisualisationProps => {
  const wire: Partial<LocalGraph> & { deleted?: boolean } = { ...local };
  delete wire.updateDate;
  delete wire.syncStatus;
  delete wire.deleted;
  if (!keepId) delete wire.id;
  return wire as VisualisationProps;
};

// NOTE: unlike ActionTypeSync's pushCreate, which repoints any actionEntries
// row referencing a just-created type's placeholder id, a graph's embedded
// `DataPoint.actionId` values (in `actionType`/`selectedActionTypes`) live
// inside a JSON blob, not a separate indexed Dexie column - there's no cheap
// way to find-and-repoint them the way `db.actionEntries.filter(...)` can.
// A graph created (and pushed) while it still references a brand-new,
// not-yet-synced local action type id is a known, narrow gap - deferred
// until graphs are actually wired up to UI writes.
const pushCreate = async (local: LocalGraph, keepalive?: boolean): Promise<void> => {
  const body = toWireProps(local, false);
  const saved = await syncFetch<VisualisationProps>({ method: 'POST', path: `${GRAPHS_PATH}/`, body, keepalive });

  await db.transaction('rw', db.graphs, async () => {
    await db.graphs.delete(local.id);
    const toStore: LocalGraph = {
      ...(saved as VisualisationProps),
      id: saved.id!,
      updateDate: DateTime.now().toISO(),
      syncStatus: 'synced',
    };
    await db.graphs.put(toStore);
  });
};

const pushUpdate = async (local: LocalGraph, keepalive?: boolean): Promise<void> => {
  const body = toWireProps(local, true);
  const saved = await syncFetch<VisualisationProps>({
    method: 'PUT',
    path: `${GRAPHS_PATH}/${local.id}`,
    body,
    keepalive,
  });

  await db.graphs.update(local.id, {
    ...(saved as VisualisationProps),
    updateDate: DateTime.now().toISO(),
    syncStatus: 'synced',
  });
};

const pushDelete = async (local: LocalGraph, keepalive?: boolean): Promise<void> => {
  await syncFetch<{ success: boolean }>({ method: 'DELETE', path: `${GRAPHS_PATH}/${local.id}`, keepalive });
  await db.graphs.delete(local.id);
};

export const pushPendingGraphs = async (options?: { keepalive?: boolean }): Promise<void> => {
  const keepalive = options?.keepalive;
  const pending = await db.graphs.filter((g) => isUnsynced(g.syncStatus)).toArray();

  for (const local of pending) {
    try {
      switch (local.syncStatus ?? 'pendingCreate') {
        case 'pendingCreate':
          await pushCreate(local, keepalive);
          break;
        case 'pendingUpdate':
          await pushUpdate(local, keepalive);
          break;
        case 'pendingDelete':
          await pushDelete(local, keepalive);
          break;
      }
    } catch (error) {
      // leave the row's syncStatus untouched so it's retried on the next cycle
      logSyncError(`failed to push graph ${local.id}`, error);
    }
  }
};

export const pullGraphs = async (): Promise<void> => {
  const cursor = await db.syncMeta.get(GRAPH_SYNC_KEY);
  const since = cursor?.lastSyncedAt ?? EPOCH;
  const { data: serverGraphs, horizon } = await syncPull<SyncedVisualisationProps[]>(
    `${GRAPHS_PATH}/sync?since=${encodeURIComponent(pullSince(since))}`
  );

  let maxLastUpdate = toUtcIso(since);

  await db.transaction('rw', db.graphs, db.syncMeta, async () => {
    for (const serverGraph of serverGraphs) {
      // compared in UTC: the API emits its own zone, and the stored cursor
      // may be a (UTC) sync horizon - see nextSyncCursor
      const updateDate = toUtcIso(serverGraph.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      if (serverGraph.deleted) {
        await db.graphs.delete(serverGraph.id!);
        continue;
      }

      const local = await db.graphs.get(serverGraph.id!);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (local?.updateDate === serverGraph.updateDate) continue;

      const toStore: LocalGraph = { ...serverGraph, id: serverGraph.id!, syncStatus: 'synced' };
      // `serverGraph` is a `SyncedVisualisationProps`, which carries `deleted`
      // - not a `LocalGraph` field, so drop it rather than let it ride along
      // as a stray property on the stored row (and later leak into a push
      // body via `toWireProps`'s `{ ...local }` spread)
      delete (toStore as Partial<SyncedVisualisationProps>).deleted;
      await db.graphs.put(toStore);
    }

    await db.syncMeta.put({ itemId: GRAPH_SYNC_KEY, lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon) });
  });
};

/**
 * Pushes any pending local graph mutations, then pulls whatever changed on
 * the server since the last sync. Push-before-pull so local changes reach
 * `synced` before we ask "what's new", minimizing cases where the pull side
 * has to skip an in-flight local row.
 */
export const syncGraphs = async (): Promise<void> => {
  await pushPendingGraphs();
  await pullGraphs();
};

export const queueLocalGraphCreate = async (props: VisualisationProps): Promise<string> => {
  const id = uuid();
  const localGraph: LocalGraph = {
    ...props,
    id,
    updateDate: DateTime.now().toISO(),
    syncStatus: 'pendingCreate',
  };

  await db.graphs.add(localGraph);
  triggerGlobalSync(GRAPH_SYNC_KEY);
  return id;
};

export const queueLocalGraphUpdate = async (id: string, changes: Partial<VisualisationProps>): Promise<void> => {
  const updated = await db.transaction('rw', db.graphs, async () => {
    const existing = await db.graphs.get(id);
    if (!existing || existing.syncStatus === 'pendingDelete') return false;

    const nextStatus: ItemSyncStatus = existing.syncStatus === 'pendingCreate' ? 'pendingCreate' : 'pendingUpdate';

    await db.graphs.update(id, {
      ...changes,
      updateDate: DateTime.now().toISO(),
      syncStatus: nextStatus,
    });
    return true;
  });

  if (updated) triggerGlobalSync(GRAPH_SYNC_KEY);
};

export const queueLocalGraphDelete = async (id: string): Promise<void> => {
  const shouldSync = await db.transaction('rw', db.graphs, async () => {
    const existing = await db.graphs.get(id);
    if (!existing) return false;

    if (existing.syncStatus === undefined || existing.syncStatus === 'pendingCreate') {
      // never reached the server - just vanish, no network call
      await db.graphs.delete(id);
      return false;
    }

    if (existing.syncStatus === 'pendingDelete') return false;

    await db.graphs.update(id, { syncStatus: 'pendingDelete' });
    return true;
  });

  if (shouldSync) triggerGlobalSync(GRAPH_SYNC_KEY);
};
